import { FilterMatchMode } from "primereact/api";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../../../context/SnackbarContext";
import {
  defaultPagination,
  getPagination,
  PaginationQueryObject,
  QuerySort,
  SortDirection,
} from "../../supportFunctions/graphql/Queries";
import { customersListQuery } from "../../supportFunctions/graphql/CustomerQueries";
import * as Permissions from "../../supportFunctions/Permissions";
import {
  Customer,
  CustomerStatusOptions,
  FullyIntegrated,
  FullyIntegratedOptions,
} from "./Customer";
import { DataTableSelectEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  Table,
  PageHeader,
  AdvancedFilterColumn,
} from "@velocity-ehs/component-library";
import { StatusBody } from "../utils/StatusBody";
import { useTranslation } from "react-i18next";
import UsersService from "@velocity-ehs/user-utils-module";
import { isEqual } from "lodash";
import GatewayService from "../../../services/GatewayService";

type CustomersProps = {
  viewProfilePermission: Array<string>;
  addPermission: Array<string>;
};

const defaultTableParams = {
  ...defaultPagination,
  rows: 20,
  sortField: "name",
  sortOrder: SortDirection.ASC,
  filters: {
    name: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    customerDomainName: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    velocityEhsCustomerNumber: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    active: {
      value: null,
      matchMode: FilterMatchMode.EQUALS,
    },
    fullyIntegrated: {
      value: [],
      matchMode: FilterMatchMode.EQUALS,
    },
  },
};

const Customers = ({
  viewProfilePermission,
  addPermission,
}: CustomersProps) => {
  const { showSnackbar } = useSnackbarActionContext();

  const history = useHistory();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [lazyParams, setLazyParams] = useState(defaultTableParams);

  const canAdd = (): boolean => {
    return Permissions.IsAllowed(addPermission, UsersService.permissions);
  };

  const canEdit = (): boolean => {
    return Permissions.IsAllowed(
      viewProfilePermission,
      UsersService.permissions
    );
  };

  const onLazyLoad = async () => {
    setIsLoading(true);

    const querySort: QuerySort =
      lazyParams.sortField && lazyParams.sortOrder
        ? {
            sortField: lazyParams.sortField,
            sortDirection: lazyParams.sortOrder,
          }
        : ({} as QuerySort);

    const paginationInput: PaginationQueryObject = getPagination(
      lazyParams.page + 1,
      lazyParams.rows,
      querySort
    );

    const getActiveFilter = () => {
      const activeFilter = lazyParams.filters.active.value;
      if (activeFilter?.length !== 1) return null;

      return activeFilter[0] === "Active";
    };

    const getFullyIntegratedFilter = () => {
      const fullyIntegratedFilter = lazyParams.filters.fullyIntegrated.value;
      if (fullyIntegratedFilter?.length !== 1) return null;
      return fullyIntegratedFilter[0] === "Yes";
    };

    const filters = {
      and: {
        active: getActiveFilter(),
        fullyIntegrated: getFullyIntegratedFilter(),
      },
      or: {
        name: lazyParams.filters["name"].value || undefined,
        domain: lazyParams.filters["customerDomainName"].value || undefined,
        velocityEhsCustomerNumber:
          lazyParams.filters["velocityEhsCustomerNumber"].value || undefined,
      },
    };

    try {
      const response = await GatewayService.api.post(
        "",
        customersListQuery(filters, paginationInput)
      );
      const data = response.data.data.customers;
      setTotalRecords(data.pageInfo.totalElements);
      setCustomers(data.items);
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    } finally {
      setIsLoading(false);
    }
  };

  const openAddCustomer = (): void => history.push("/customer/add");

  const onChange = (event: any): void => {
    setLazyParams(event);
  };

  const onStatusFilter = (filterValue: Array<"Active" | "Inactive">): void => {
    setLazyParams((state: any) => ({
      ...state,
      ...defaultPagination,
      filters: {
        ...state.filters,
        active: {
          value: filterValue,
          matchMode: FilterMatchMode.EQUALS,
        },
      },
    }));
  };

  const onGlobalSearch = (event: any): void => {
    const value = event.target.value;
    setLazyParams((state: any) => {
      return {
        ...state,
        ...defaultPagination,
        filters: {
          ...state.filters,
          name: {
            value,
            matchMode: FilterMatchMode.CONTAINS,
          },
          customerDomainName: {
            value,
            matchMode: FilterMatchMode.CONTAINS,
          },
          velocityEhsCustomerNumber: {
            value,
            matchMode: FilterMatchMode.CONTAINS,
          },
        },
      };
    });
  };

  const onGlobalSearchCallback = useCallback((event: any) => {
    onGlobalSearch(event);
  }, []);

  const onRowSelect = (event: DataTableSelectEvent): void => {
    const editPermitted = canEdit();

    if (editPermitted) return history.push(`/customer/${event.data.id}/edit`);

    return history.push(`/customer/${event.data.id}`);
  };

  useEffect(() => {
    onLazyLoad();
    // eslint-disable-next-line
  }, [lazyParams]);

  const handleClearFilters = () => {
    setLazyParams(defaultTableParams);
  };

  const isDefaultFilter = isEqual(
    defaultTableParams.filters,
    lazyParams.filters
  );

  // Handler for fully integrated filter
  const onFullyIntegratedFilter = (filterValue: string[]): void => {
    setLazyParams((state: any) => ({
      ...state,
      ...defaultPagination,
      filters: {
        ...state.filters,
        fullyIntegrated: {
          value: filterValue,
          // matchMode: FilterMatchMode.EQUALS,
        },
      },
    }));
  };

  return (
    <>
      <PageHeader
        title={t("universal_Customers")}
        navigationTree={[
          { text: `Accelerate ${t("universal_Home")}`, href: "/" },
          { text: t("universal_Customers"), href: "" },
        ]}
        primaryButton={{
          label: t("universal_Add"),
          id: "addcustomer",
          visible: canAdd(),
          onClick: openAddCustomer,
        }}
      />
      <div className="card__fullContent">
        <Table
          data={customers}
          params={lazyParams}
          totalRecords={totalRecords}
          selectionMode="single"
          onRowSelect={onRowSelect}
          onPage={onChange}
          onSort={onChange}
          onGlobalSearch={onGlobalSearchCallback}
          isLoading={isLoading}
          clearFilterCallback={handleClearFilters}
          isClearFiltersButtonDisabled={isDefaultFilter}
        >
          <Column field="name" header={t("universal_Name")} sortable />
          <Column
            field="customerDomainName"
            sortable
            header={t("universal_Domain")}
          />
          <Column
            field="velocityEhsCustomerNumber"
            sortable
            header={t("velocityEhsCustomerNumber")}
          />
          {AdvancedFilterColumn({
            field: "fullyIntegrated",
            filterField: "fullyIntegrated",
            header: t("customer_form_fully_integrated"),
            options: FullyIntegratedOptions,
            body: (rowData) => (rowData.fullyIntegrated ? "Yes" : "No"),
            onApply: onFullyIntegratedFilter,
            filterParams: lazyParams.filters,
          })}

          {AdvancedFilterColumn({
            field: "active",
            filterField: "active",
            header: t("universal_Status"),
            options: CustomerStatusOptions,
            body: StatusBody,
            onApply: onStatusFilter,
            filterParams: lazyParams.filters,
          })}
        </Table>
      </div>
    </>
  );
};

export default Customers;
